import { supabase } from '../lib/supabase';
import { contractValidation } from '../utils/validationUtils';

export const contractService = {
  // Get all contracts with related data
  async getAllContracts(userId) {
    try {
      const { data, error } = await supabase?.from('contracts')?.select(`
          *,
          property:properties(
            id,
            title,
            property_type,
            address,
            city,
            district,
            area,
            price,
            images
          ),
          client:clients(
            id,
            name,
            email,
            phone,
            category
          ),
          marketing_contract:marketing_contracts(*),
          buyer_contract:buyer_representation_contracts(*),
          approvals:contract_approvals(
            id,
            approver_name,
            approver_role,
            action,
            comments,
            created_at
          ),
          versions:contract_versions(
            id,
            version_number,
            created_at
          )
        `)?.eq('user_id', userId)?.order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching contracts:', error);
      throw error;
    }
  },

  // Get single contract with full details
  async getContractById(contractId) {
    try {
      const { data, error } = await supabase?.from('contracts')?.select(`
          *,
          property:properties(
            *,
            owner:owners(*)
          ),
          client:clients(*),
          marketing_contract:marketing_contracts(*),
          buyer_contract:buyer_representation_contracts(*),
          approvals:contract_approvals(
            *,
            approver:user_profiles(full_name, email, role)
          ),
          versions:contract_versions(*),
          modifications:contract_modifications(*),
          signatures:contract_signatures(*)
        `)?.eq('id', contractId)?.single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error fetching contract:', error);
      throw error;
    }
  },

  // Create new contract
  async createContract(contractData, userId) {
    try {
      // SERVER-SIDE VALIDATION
      const validation = contractValidation?.validate(contractData);
      if (!validation?.isValid) {
        const errorMessages = Object.values(validation?.errors)?.join('، ');
        throw new Error(`خطأ في البيانات: ${errorMessages}`);
      }

      const { data, error } = await supabase?.from('contracts')?.insert({
          user_id: userId,
          property_id: contractData?.propertyId,
          client_id: contractData?.clientId,
          contract_number: contractData?.contractNumber,
          contract_type: contractData?.contractType,
          status: contractData?.status || 'pending',
          start_date: contractData?.startDate,
          end_date: contractData?.endDate,
          amount: contractData?.amount,
          terms: contractData?.terms,
          documents: contractData?.documents || []
        })?.select()?.single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error creating contract:', error);
      throw error;
    }
  },

  // Update contract status
  async updateContractStatus(contractId, status) {
    try {
      const { data, error } = await supabase?.from('contracts')?.update({ 
          status,
          updated_at: new Date()?.toISOString()
        })?.eq('id', contractId)?.select()?.single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error updating contract status:', error);
      throw error;
    }
  },

  // Add contract approval
  async addContractApproval(approvalData, userId) {
    try {
      const { data, error } = await supabase?.from('contract_approvals')?.insert({
          contract_id: approvalData?.contractId,
          version_id: approvalData?.versionId,
          modification_id: approvalData?.modificationId,
          approver_id: userId,
          approver_name: approvalData?.approverName,
          approver_role: approvalData?.approverRole,
          action: approvalData?.action,
          comments: approvalData?.comments,
          ip_address: approvalData?.ipAddress
        })?.select()?.single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error adding contract approval:', error);
      throw error;
    }
  },

  // Get contract statistics
  async getContractStats(userId) {
    try {
      const { data, error } = await supabase?.from('contracts')?.select('status')?.eq('user_id', userId);

      if (error) throw error;

      const stats = {
        pendingReview: data?.filter(c => c?.status === 'pending')?.length || 0,
        approved: data?.filter(c => c?.status === 'approved')?.length || 0,
        rejected: data?.filter(c => c?.status === 'rejected')?.length || 0,
        active: data?.filter(c => c?.status === 'active')?.length || 0,
        total: data?.length || 0
      };

      return stats;
    } catch (error) {
      console.error('Error fetching contract stats:', error);
      throw error;
    }
  },

  // Filter contracts
  async filterContracts(userId, filters) {
    try {
      let query = supabase?.from('contracts')?.select(`
          *,
          property:properties(title, property_type, address, city, images),
          client:clients(name, email, phone),
          marketing_contract:marketing_contracts(*),
          buyer_contract:buyer_representation_contracts(*)
        `)?.eq('user_id', userId);

      // Apply status filter
      if (filters?.status && filters?.status !== 'all') {
        query = query?.eq('status', filters?.status);
      }

      // Apply search filter
      if (filters?.search) {
        query = query?.or(`contract_number.ilike.%${filters?.search}%`);
      }

      // Apply contract type filter
      if (filters?.contractType && filters?.contractType !== 'all') {
        query = query?.eq('contract_type', filters?.contractType);
      }

      const { data, error } = await query?.order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error filtering contracts:', error);
      throw error;
    }
  }
};
